"use client";

import { useEffect, useState } from "react";
import { enqueueTag } from "./tagQueue";

/**
 * Releases `total` tag stages one idle slice at a time, in order.
 *
 * PERF: `strategy="lazyOnload"` defers a `<Script>` until `window.load`, and the
 * analytics gate then released every tag in the SAME frame — GTM, GA, the tenant's
 * server-side GTM container, three social pixels and the Smartlook recorder all
 * starting at once, on top of our own hydration. Measured on the home page that
 * burst produced 67 long tasks totalling 7,829ms.
 *
 * Deferring harder would not have helped: the tags were already deferred, they were
 * just not *spread out*. Returning a stage counter lets the caller render its scripts
 * progressively, so each gets its own idle slice and the browser can paint and
 * respond in between.
 *
 * Put attribution-critical tags in the earliest stages — the queue preserves order,
 * so the last stage waits the longest.
 */
export const useTagStages = (total: number, enabled: boolean) => {
  const [released, setReleased] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    for (let i = 0; i < total; i++) {
      enqueueTag(() => {
        if (cancelled) return;
        setReleased((r) => (r < total ? r + 1 : r));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [enabled, total]);

  return released;
};

export default useTagStages;
